import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import "./ServiceDetail.css";
import { config } from "../config";

const TECHS = [
  "React",
  "Next.js",
  "WordPress",
  "Shopify",
  "Node.js",
  "MongoDB",
  "Tailwind CSS",
  "TypeScript",
  "Framer Motion",
  "Figma",
  "Webflow",
  "PHP",
];

const FEATURES = [
  {
    icon: "🎨",
    title: "Custom UI/UX Design",
    desc: "Unique, brand-aligned interfaces designed in Figma - every pixel crafted with purpose and precision.",
  },
  {
    icon: "⚡",
    title: "Blazing Performance",
    desc: "Optimised for Core Web Vitals, sub-2s load times, lazy loading, and Google PageSpeed scores above 95.",
  },
  {
    icon: "📱",
    title: "Mobile-First Responsive",
    desc: "Flawless across every screen - from 4K monitors to the smallest smartphones.",
  },
  {
    icon: "🔍",
    title: "SEO Architecture",
    desc: "Clean semantic HTML, structured data, sitemap, robots.txt and technical SEO baked in from day one.",
  },
  {
    icon: "🔒",
    title: "Security & SSL",
    desc: "SSL certificates, HTTPS enforcement, input sanitisation and best-practice security hardening.",
  },
  {
    icon: "⚙️",
    title: "CMS Integration",
    desc: "Easy-to-manage content via WordPress, Sanity, or a custom CMS - update your site without a developer.",
  },
];

const PORTFOLIO = [
  {
    title: "Roliox Alloys",
    type: "Web App",
    tech: "React",
    color: "var(--gold)",
    image:
      "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=600&q=80",
    url: "https://rolioxalloys.in/",
  },
  {
    title: "Maa Vaishno Jawelry",
    type: "Web App",
    tech: "React",
    color: "var(--accent2)",
    image:
      "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80",
    url: "https://maavaishnojewellers.in/",
  },
  {
    title: "Elementeers",
    type: "Healthcare Platform",
    tech: "React + Laravel",
    color: "var(--accent)",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80",
    url: "https://www.elementeers.com/",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Discovery Call",
    desc: "We understand your business, goals, target audience and technical requirements inside-out before writing a single line of code.",
  },
  {
    n: "02",
    title: "Design & Prototype",
    desc: "Wireframes then high-fidelity Figma designs. You approve every screen before development begins.",
  },
  {
    n: "03",
    title: "Development",
    desc: "Clean, well-documented code built with modern frameworks. Regular previews so you are never in the dark.",
  },
  {
    n: "04",
    title: "QA & Testing",
    desc: "Cross-browser, cross-device testing, performance audits, accessibility checks and SEO validation.",
  },
  {
    n: "05",
    title: "Launch",
    desc: "Smooth deployment with zero downtime. DNS setup, SSL, analytics and final checks - all handled.",
  },
  {
    n: "06",
    title: "Support & Growth",
    desc: "Post-launch support, analytics review, and ongoing optimisation to keep your site performing at its best.",
  },
];

const FAQS = [
  {
    q: "How long does a website take to build?",
    a: "Typically 2–3 weeks for a Starter site, 4–6 weeks for Professional, and 8–16 weeks for Enterprise projects. Timelines depend on complexity and content readiness.",
  },
  {
    q: "Do you build e-commerce websites?",
    a: "Absolutely. We build full e-commerce platforms with product management, payment gateways (Razorpay, Stripe, PayPal), inventory and order management.",
  },
  {
    q: "Will I be able to update the site myself?",
    a: "Yes. We integrate a user-friendly CMS (usually WordPress or Sanity) so you can update content, images, and blog posts without any coding knowledge.",
  },
  {
    q: "Do you offer post-launch support?",
    a: "All packages include post-launch support - 1 month for Starter, 3 months for Professional, and 6 months for Enterprise. Extended maintenance plans are also available.",
  },
];

export default function WebDevelopment() {
  useReveal();
  const marquee = [...TECHS, ...TECHS];

  return (
    <div className="sd-page">
      {/* HERO */}
      <section className="sd-hero">
        <div className="sd-hero__bg" />
        <div className="sd-hero__grid" />
        <div className="container sd-hero__inner">
          <div className="sd-hero__content">
            <span className="sd-eyebrow">Website Development</span>
            <h1 className="sd-hero__title">
              Websites That Convert
              <br />
              Visitors Into <span className="g">Customers</span>
            </h1>
            <p className="sd-hero__sub">
              We engineer fast, secure, and stunning websites using the latest
              technologies. From elegant landing pages to complex web
              applications - built to perform, designed to impress.
            </p>
            <div className="sd-hero__btns">
              <Link to={`${config.appUrl}contact`} className="btn-primary">
                Start Your Project →
              </Link>
              <Link
                to={`${config.appUrl}web-development-package`}
                className="btn-outline"
              >
                View Pricing
              </Link>
            </div>
            <div className="sd-hero__stats">
              {/* <div className="sd-stat"><span>150+</span><p>Sites Launched</p></div> */}
              <div className="sd-stat">
                <span>99%</span>
                <p>Client Satisfaction</p>
              </div>
              <div className="sd-stat">
                <span>90+</span>
                <p>Avg PageSpeed Score</p>
              </div>
            </div>
          </div>
          <div className="sd-hero__visual rr">
            <div className="sd-mockup">
              <div className="sd-mockup__bar">
                <span />
                <span />
                <span />
              </div>
              <div className="sd-mockup__body">
                <div className="sd-mockup__hero-blk" />
                <div className="sd-mockup__row">
                  <div className="sd-mockup__card" />
                  <div className="sd-mockup__card" />
                  <div className="sd-mockup__card" />
                </div>
                <div className="sd-mockup__text-blk" />
                <div className="sd-mockup__text-blk sd-mockup__text-blk--sm" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TECH MARQUEE */}
      <div className="marquee-section">
        <div className="marquee-track">
          {marquee.map((t, i) => (
            <div className="marquee-item" key={i}>
              <span className="dot" />
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <section className="sd-features">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">What You Get</span>
            <h2 className="s-title">
              Built for Performance,{" "}
              <span className="g">Designed for Impact</span>
            </h2>
            <p className="s-body">
              Every website we build is engineered from the ground up for speed,
              security, and conversions.
            </p>
          </div>
          <div className="sd-feat-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`sd-feat-card reveal d${(i % 3) + 1}`}
              >
                <div className="sd-feat-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="sd-portfolio">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Our Work</span>
            <h2 className="s-title">
              Recent <span className="g">Projects</span>
            </h2>
          </div>
          <div className="sd-port-grid">
            {PORTFOLIO.map((p, i) => (
              <div
                key={p.title}
                className={`sd-port-card reveal d${(i % 3) + 1}`}
                style={{ "--pc": p.color }}
              >
                {/* <div className="sd-port-card__img">
                  <img src={p.image} alt={p.title} />
                </div> */}
                <div className="sd-port-card__body">
                  <div className="sd-port-card__top">
                    <span className="sd-port-tag">{p.type}</span>
                  </div>
                  <h3>{p.title}</h3>
                  <p className="sd-port-tech">{p.tech}</p>
                  <Link
                    to={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sd-port-arrow"
                  >
                    View Case Study →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="sd-process">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">How We Work</span>
            <h2 className="s-title">
              Our Development <span className="g">Process</span>
            </h2>
          </div>
          <div className="sd-proc-grid">
            {PROCESS.map((p, i) => (
              <div
                key={p.n}
                className={`sd-proc-card reveal d${(i % 3) + 1}`}
                data-n={p.n}
              >
                <div className="proc-num">{p.n}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sd-faq">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Got Questions?</span>
            <h2 className="s-title">
              Common <span className="g">Questions</span>
            </h2>
          </div>
          <div className="sd-faq-grid">
            {FAQS.map((f, i) => (
              <div key={i} className={`faq-card reveal d${(i % 2) + 1}`}>
                <div className="faq-q">
                  <span className="faq-icon">✦</span>
                  {f.q}
                </div>
                <p className="faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-sect">
        <div className="cta-glow" />
        <div
          className="container"
          style={{ position: "relative", textAlign: "center" }}
        >
          <div className="reveal">
            <h2 className="cta-title">
              Ready to Build Your <span className="g">Dream Website?</span>
            </h2>
            <p className="cta-body">
              Get a free consultation and project estimate - no obligation, no
              fluff.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Link to={`${config.appUrl}contact`} className="btn-primary">
                Get Free Quote →
              </Link>
              <Link
                to={`${config.appUrl}web-development-package`}
                className="btn-outline"
              >
                View Packages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
